import clsx from 'clsx';
import React from 'react';
import { Button } from './Button';
import { useEmbedded } from './Embed';
import { Callout } from './Feedback';

/**
 * Form layout primitives for editors and settings pages.
 *
 * - FormGroup: title + one-line description in a 128px left column, fields
 *   in a grid on the right. Grouping comes from layout, not boxes.
 * - SaveBar: sticky footer that appears only when the form is dirty. One
 *   primary Save, one ghost Discard, inline error. Never a toast.
 */
export const FormGroup: React.FC<{
  title: React.ReactNode;
  description?: React.ReactNode;
  cols?: 1 | 2 | 4;
  children: React.ReactNode;
  className?: string;
}> = ({ title, description, cols = 2, children, className }) => (
  <div
    role="group"
    className={clsx(
      'grid grid-cols-1 gap-y-2 sm:grid-cols-[128px_minmax(0,1fr)] sm:gap-x-6',
      className,
    )}
  >
    <div className="pt-0.5">
      <div className="text-v2-12 font-semibold text-v2-fg-2">{title}</div>
      {description && (
        <p className="mt-0.5 text-v2-12 leading-[18px] text-v2-fg-4">
          {description}
        </p>
      )}
    </div>
    <div
      className={clsx(
        'grid gap-x-4 gap-y-3',
        cols === 1 ? 'grid-cols-1' : 'grid-cols-2',
        cols === 4
          ? 'sm:grid-cols-4'
          : cols === 2
            ? 'sm:grid-cols-2'
            : undefined,
      )}
    >
      {children}
    </div>
  </div>
);

export const SaveBar: React.FC<{
  dirty: boolean;
  saving?: boolean;
  error?: string | null;
  onSave: () => void;
  onDiscard: () => void;
  saveLabel?: string;
  /** Short summary of what changed, e.g. "3 fields changed". */
  summary?: string;
}> = ({
  dirty,
  saving,
  error,
  onSave,
  onDiscard,
  saveLabel = 'Save changes',
  summary,
}) => {
  const embedded = useEmbedded();
  if (!dirty && !error) return null;
  return (
    <div
      className={clsx(
        embedded
          ? 'v2-rise-in mt-4 rounded-v2-lg border border-v2-line bg-v2-sunken/60 px-3 py-2'
          : 'v2-fade-in sticky bottom-0 z-[4] border-t border-v2-line bg-v2-surface/95 px-4 py-2.5 backdrop-blur-sm lg:px-6',
      )}
    >
      <div className="flex max-w-[1160px] items-center gap-3">
        {error ? (
          <Callout tone="danger" className="flex-1 py-1.5">
            {error}
          </Callout>
        ) : (
          <span className="text-v2-12 text-v2-fg-3">
            {summary ?? 'Unsaved changes'}
          </span>
        )}
        <div className="ml-auto flex items-center gap-2">
          <Button variant="ghost" onClick={onDiscard} disabled={saving}>
            Discard
          </Button>
          <Button variant="primary" onClick={onSave} loading={saving}>
            {saveLabel}
          </Button>
        </div>
      </div>
    </div>
  );
};
